import { useContext, useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { SidebarContext } from "src/contexts/SidebarContext";
import { Logo } from "src/components/Logo";
import ThemeModeSwitch from "src/components/ThemeModeSwitch";
import { ThemeContext } from "src/theme/ThemeProvider";

import { Box, Button, Drawer, List, ListItem, Tooltip, Typography } from "@mui/material";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import FlareIcon from "@mui/icons-material/Flare";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import SettingsInputAntennaIcon from "@mui/icons-material/SettingsInputAntenna";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ListAltIcon from '@mui/icons-material/ListAlt';
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";

import { styled } from "@mui/material/styles";
import { listConnectors } from "src/handlers/Telemetry";
import { getActiveProjectId } from "src/handlers/Projects";

const SidebarWrapper = styled(Box)(
  ({ theme }) => `
        width: ${theme.sidebar.width};
        color: ${theme.sidebar.textColor};
        background: ${theme.sidebar.background};
        box-shadow: ${theme.sidebar.boxShadow};
        height: 100%;
        display: flex;
        flex-direction: column;

        @media (min-width: ${theme.breakpoints.values.lg}px) {
            position: fixed;
            z-index: 10;
            border-top-right-radius: ${theme.general.borderRadius};
            border-bottom-right-radius: ${theme.general.borderRadius};
        }
`
);

const TopSection = styled(Box)(
  ({ theme }) => `
        display: flex;
        height: 88px;
        align-items: center;
        margin: 0 ${theme.spacing(2)} ${theme.spacing(2)};
        border-bottom: ${theme.sidebar.dividerBg} solid 1px;
`
);

const FooterSection = styled(Box)(
  ({ theme }) => `
    margin-top: auto;
    padding: ${theme.spacing(3, 2, 4)};
    border-top: ${theme.sidebar.dividerBg} solid 1px;
    display: flex;
    flex-direction: column;
    align-items: center;
`
);

const SidebarBody = styled(Box)(
  () => `
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
`
);

const MenuWrapper = styled(List)(
  ({ theme }) => `
    margin-bottom: ${theme.spacing(1)};
    padding: 0;

    .MuiButton-root {
      width: 100%;
      font-weight: 500;
      font-size: ${theme.typography.pxToRem(13)};
      padding: ${theme.spacing(1, 2)};
      line-height: 1.5;
      justify-content: flex-start;
      border-radius: 10px;
      transition: background-color 150ms ease, color 150ms ease;
    }
`
);

const SIDEBAR_GROUPS = [
  {
    label: "Overview",
    items: [
      {
        id: "configurations",
        label: "Configurations",
        to: "/components/configurations",
        icon: GridViewOutlinedIcon,
        hideWhenFabric: true
      },
      {
        id: "fabric",
        label: "Fabric",
        to: "/components/fabric",
        icon: FlareIcon,
        requiresConnector: true,
        requiresAuth: true
      }
    ]
  },
  {
    label: "Configuration",
    items: [
      {
        id: "vlans",
        label: "VLANs",
        to: "/components/vlan",
        icon: AltRouteIcon
      },
      {
        id: "routers",
        label: "Routers",
        to: "/components/router",
        icon: HubOutlinedIcon
      },
      {
        id: "switches",
        label: "Switches",
        to: "/components/switch",
        icon: DeviceHubIcon
      }
    ]
  },
  {
    label: "Operations",
    items: [
      {
        id: "inventory",
        label: "Inventory",
        to: "/components/inventory",
        icon: ListAltIcon,
        requiresConnector: true,
        requiresAuth: true
      },
      {
        id: "connectors",
        label: "Connectors",
        to: "/components/connectors",
        icon: SettingsInputAntennaIcon
      },
      {
        id: "deployment",
        label: "Deployments",
        to: "/components/deployment",
        icon: RocketLaunchIcon,
        requiresConnector: true
      }
    ]
  }
];

function matchesSidebarPath(pathname, to) {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function SidebarNavigationItem({ item, active, onNavigate }) {
  const Icon = item.icon;

  const button = (
    <Button
      disableRipple
      size="large"
      component={item.disabled ? "button" : NavLink}
      to={item.disabled ? undefined : item.to}
      onClick={item.disabled ? undefined : onNavigate}
      disabled={item.disabled}
      startIcon={<Icon sx={{ fontSize: 20 }} />}
      aria-disabled={item.disabled || undefined}
      sx={(theme) => ({
        backgroundColor: active ? theme.sidebar.menuItemBgActive : "transparent",
        color: active ? theme.sidebar.menuItemIconColorActive : theme.sidebar.menuItemColor,
        fontWeight: active ? 600 : 500,
        borderLeft: "3px solid transparent",
        opacity: item.disabled ? 0.55 : 1,
        cursor: item.disabled ? "not-allowed" : "pointer",
        "& .MuiButton-startIcon": {
          color: active ? theme.sidebar.menuItemIconColorActive : theme.sidebar.menuItemIconColor,
          transition: "color 150ms ease"
        },
        "&:hover": {
          backgroundColor:
            active
              ? theme.sidebar.menuItemBgActive
              : item.disabled
                ? "transparent"
                : theme.palette.mode === "light"
                  ? "rgba(59, 130, 246, 0.08)"
                  : "rgba(255,255,255,0.06)"
        },
        "&.Mui-disabled": {
          color: theme.sidebar.menuItemColor,
          pointerEvents: "auto"
        }
      })}
    >
      {item.label}
    </Button>
  );

  const wrapped = item.disabled && item.lockedReason ? (
    <Tooltip title={item.lockedReason} placement="right" arrow>
      <span style={{ display: "flex", width: "100%" }}>{button}</span>
    </Tooltip>
  ) : button;

  return (
    <ListItem component="div" disablePadding sx={{ px: 2, py: 0.35 }}>
      {wrapped}
    </ListItem>
  );
}

function SidebarNavigation({ onNavigate, pathname, groups }) {
  return (
    <MenuWrapper component="nav">
      {groups.map((group, gi) => (
        <Box key={group.label}>
          {gi > 0 && (
            <Box sx={{ mx: 2, my: 0.5, borderTop: (theme) => `1px solid ${theme.sidebar.dividerBg}` }} />
          )}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              px: 3,
              pt: gi === 0 ? 0.5 : 1.5,
              pb: 0.5,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "text.secondary",
              fontSize: "0.68rem",
              fontWeight: 700
            }}
          >
            {group.label}
          </Typography>
          {group.items.map((item) => (
            <SidebarNavigationItem
              key={item.id}
              item={item}
              active={!item.disabled && matchesSidebarPath(pathname, item.to)}
              onNavigate={onNavigate}
            />
          ))}
        </Box>
      ))}
    </MenuWrapper>
  );
}

function Sidebar() {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const { mode, resolvedMode, setMode } = useContext(ThemeContext);
  const location = useLocation();
  const [hasConnector, setHasConnector] = useState(
    () => localStorage.getItem("hasConnectors") === "1"
  );
  const [isSignedIn, setIsSignedIn] = useState(() => Boolean(localStorage.getItem("sessioninfo")));
  const [projectId, setProjectId] = useState(() => getActiveProjectId());

  // Re-check connector count on mount, project switch, and sign-in
  useEffect(() => {
    let cancelled = false;
    if (!isSignedIn) {
      setHasConnector(false);
      return;
    }
    // Optimistically clear while loading for a new project
    setHasConnector(false);
    (async () => {
      try {
        const connectors = await listConnectors();
        if (!cancelled) setHasConnector(Array.isArray(connectors) && connectors.length > 0);
      } catch {
        if (!cancelled) setHasConnector(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isSignedIn, projectId]);

  // Pick up updates from getConnectorSummary calls within the same tab
  useEffect(() => {
    const onChanged = (e) => setHasConnector(e.detail);
    const onStorage = (e) => {
      if (e.key === "sessioninfo") setIsSignedIn(Boolean(localStorage.getItem("sessioninfo")));
      if (e.key === "hasConnectors") setHasConnector(localStorage.getItem("hasConnectors") === "1");
      // sessionStorage keys fire with key===null on dispatchEvent(new Event("storage"))
      if (e.key === null || e.key === "projectid") setProjectId(getActiveProjectId());
    };
    const onProjectSwitched = async (e) => {
      const pid = e.detail?.projectId;
      if (!pid) return;
      try {
        const connectors = await listConnectors(pid);
        const hasAny = Array.isArray(connectors) && connectors.length > 0;
        localStorage.setItem("hasConnectors", hasAny ? "1" : "0");
        localStorage.setItem(`hasConnectors_${pid}`, hasAny ? "1" : "0");
        setHasConnector(hasAny);
      } catch {
        // keep cached value
      }
    };
    window.addEventListener("hasConnectorsChanged", onChanged);
    window.addEventListener("storage", onStorage);
    window.addEventListener("projectSwitched", onProjectSwitched);
    return () => {
      window.removeEventListener("hasConnectorsChanged", onChanged);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("projectSwitched", onProjectSwitched);
    };
  }, []);
  const closeSidebar = () => toggleSidebar();
  const handleNavigate = () => {
    if (sidebarToggle) {
      closeSidebar();
    }
  };

  // items that require a connector or auth stay visible so new
  // users can see what's available. They're rendered disabled with a tooltip
  // explaining what unlocks them.
  const groups = useMemo(
    () =>
      SIDEBAR_GROUPS
        .map((group) => ({
          ...group,
          items: group.items
            .map((item) => {
              const fabricActive = hasConnector && isSignedIn;
              if (item.hideWhenFabric && fabricActive) return null;

              const requiresAuthUnmet = item.requiresAuth && !isSignedIn;
              const requiresConnectorUnmet = item.requiresConnector && !hasConnector;

              if (!requiresAuthUnmet && !requiresConnectorUnmet) {
                return { ...item, disabled: false };
              }

              const reasons = [];
              if (requiresAuthUnmet) reasons.push("Sign in to unlock.");
              if (requiresConnectorUnmet) reasons.push("Install a connector to unlock.");

              return {
                ...item,
                disabled: true,
                lockedReason: reasons.join(" ")
              };
            })
            .filter(Boolean)
        }))
        .filter((group) => group.items.length > 0),
    [hasConnector, isSignedIn]
  );

  // Lottie switch reflects the *resolved* mode (what's actually
  // rendering) and clicking it sets a manual preference. The Auto chip
  // toggles between "auto" (follow OS) and the current manual mode.
  const isAutoMode = mode === "auto";
  const handleAutoToggle = () => {
    if (isAutoMode) {
      setMode(resolvedMode);
    } else {
      setMode("auto");
    }
  };

  const footer = (
    <FooterSection>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mb: 2.25,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "text.secondary",
          textAlign: "center"
        }}
      >
        Appearance
      </Typography>
      <ThemeModeSwitch
        isDarkMode={resolvedMode === "dark"}
        onChange={(isDarkMode) => setMode(isDarkMode ? "dark" : "light")}
      />
      <Tooltip
        arrow
        placement="right"
        title={
          isAutoMode
            ? "Currently following your system preference. Click to stop."
            : "Follow the system color scheme."
        }
      >
        <Box
          component="button"
          type="button"
          onClick={handleAutoToggle}
          aria-pressed={isAutoMode}
          sx={(theme) => ({
            mt: 1.5,
            px: 1.25,
            py: 0.4,
            borderRadius: "999px",
            border: `1px solid ${isAutoMode ? theme.palette.primary.main : theme.sidebar.dividerBg}`,
            background: isAutoMode
              ? (theme.palette.mode === "light"
                  ? "rgba(140, 124, 240, 0.12)"
                  : "rgba(140, 124, 240, 0.22)")
              : "transparent",
            color: isAutoMode ? theme.palette.primary.main : theme.sidebar.menuItemColor,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            cursor: "pointer",
            transition: "all 160ms ease"
          })}
        >
          Auto
        </Box>
      </Tooltip>
    </FooterSection>
  );

  return (
    <>
      <Box sx={{ display: { lg: 'block', xs: 'none' } }}>
        <SidebarWrapper>
          <TopSection>
            <Logo />
          </TopSection>
          <SidebarBody>
            <SidebarNavigation onNavigate={handleNavigate} pathname={location.pathname} groups={groups} />
            {footer}
          </SidebarBody>
        </SidebarWrapper>
      </Box>
      <Box sx={{ display: { lg: 'none', xs: 'block' } }}>
        <Drawer
          anchor="left"
          open={sidebarToggle}
          onClose={closeSidebar}
          variant="temporary"
          elevation={9}
        >
          <SidebarWrapper>
            <TopSection>
              <Logo />
            </TopSection>
            <SidebarBody>
              <SidebarNavigation onNavigate={handleNavigate} pathname={location.pathname} groups={groups} />
              {footer}
            </SidebarBody>
          </SidebarWrapper>
        </Drawer>
      </Box>
    </>
  );
}

export default Sidebar;
